import { EQuizMode } from '@constants';
import type { IStudentQuestion } from '@sdk/contracts';
import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListChildComponentProps } from 'react-window';

import { PracticalCaseDescription } from '../PracticalCaseDescription';
import { QuestionItem } from '../QuestionItem';

import { RowInner, RowOuter, HeaderWrapper, TitleWrapper } from './VirtualizedRow.styles';

import { useQuizInfo } from '@/hooks';

interface IListData {
  items: readonly IStudentQuestion[];
  hasPracticalCase: boolean;
  setItemSize?: (index: number, size: number) => void;
}

type RowProps = ListChildComponentProps<IListData>;

export const VirtualizedRow = memo(({ index, style, data }: RowProps) => {
  const { t } = useTranslation('StudentQuizTranslations');
  const { studentQuiz } = useQuizInfo();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!data.setItemSize) return;
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const height = el.getBoundingClientRect().height;
      data.setItemSize?.(index, Math.ceil(height + 32));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [index, data.setItemSize]);

  if (index === 0) {
    return (
      <div style={style} data-row-type="practical-case">
        <RowOuter ref={containerRef}>
          <HeaderWrapper>
            <TitleWrapper>{studentQuiz?.parentQuiz?.title ?? ''}</TitleWrapper>
            {studentQuiz?.mode === EQuizMode.PRACTICAL_CASE && <div>{t('READ_CASE')}</div>}
          </HeaderWrapper>
          <RowInner>
            <div style={{ width: '100%' }}>
              {studentQuiz?.mode === EQuizMode.PRACTICAL_CASE ? <PracticalCaseDescription /> : null}
            </div>
          </RowInner>
        </RowOuter>
      </div>
    );
  }

  const effectiveIndex = index - 1;
  const question = data.items[effectiveIndex];
  return (
    <div
      style={style}
      data-question-index={effectiveIndex}
      data-question-id={question.question._id}>
      <RowOuter ref={containerRef}>
        <RowInner>
          <div style={{ width: '100%' }}>
            <QuestionItem index={effectiveIndex} studentQuestion={question} />
          </div>
        </RowInner>
      </RowOuter>
    </div>
  );
});
